import { Fn }			from "ts-base/fn";
import { Endo }			from "ts-base/endo";
import { Branded }		from "ts-base/branded";
import { Nullable }		from "ts-base/nullable";
import { Arrays }		from "ts-base/arrays";
import { Numbers }		from "ts-base/numbers";
import { Booleans }		from "ts-base/booleans";
import { Maybe }		from "ts-base/maybe";
import { Comparator }	from "ts-base/comparator";
import { Zoomer }		from "ts-base/zoomer";
import { Json }			from "ts-base/json";

import { Refresh }		from "@v3/preact/refresh";
import { FormDom }		from "@v3/geko/form/form";

import { Logger }		from "@geotoura/shared/logger";
import * as i18n		from "@geotoura/shared/i18n";
import * as upModel		from "@geotoura/shared/upModel";
import * as commonModel	from "@geotoura/shared/commonModel";

import * as Server		from "@geotoura/common/Server";
import * as tracking	from "@geotoura/common/privacy/tracking";

import * as util		from "@geotoura/up/util";
import { UpData }		from "@geotoura/up/upData";
import {
	destinationState,
	galleryDefault,
	routeVersion,
} from "@geotoura/up/constants";

import {
	Model,
	Route,
	ScreenSize,
	PersonalSchema,	PersonalModel,
	FlightSchema,	FlightModel,
	DestinationState,
	DrawerState,
	DrawerMode,
	ExampleRouteModel,
	setMapViewEvent,
} from "@geotoura/up/model";
import * as routes		from "@geotoura/up/routes";
import { calcDuration }	from "@geotoura/up/initHelpers";

const logger	= Logger.create("up-actions");

//-----------------------------------------------------------------------------
//## refresh

let handle:Refresh<Model>	= Refresh.notInstalled();

export const setRefresh	= (it:Refresh<Model>):void => { handle = it; };

const refresh:Refresh<Model>	= (change:Endo<Model>):void => handle(change);

//------------------------------------------------------------------------------
// The route is saved in local storage. Every action which changes the route MUST also update local storage

const updateLocalStorage = (route:Route):void => {
	const routeToStore	= routes.regularItems(route);
	const routeString	= Json.stringify(routeToStore);
	localStorage.setItem("route",			routeString);
	localStorage.setItem("routeVersion",	routeVersion);
};

// -------------------------------------------------------------------------------
// Zoom functions
// -------------------------------------------------------------------------------

const modelZoomer	=
	Zoomer.on<Model>();

const exampleRouteZoomer:Zoomer<Model, ExampleRouteModel>	=
	modelZoomer.atKey("exampleRouteData");

const personalZoomer:Zoomer<Model, PersonalModel>	=
	modelZoomer.atKey("personalData");

const flightZoomer:Zoomer<Model, FlightModel>	=
	modelZoomer.atKey("flightData");

const routeZoomer:Zoomer<Model, Route>	=
	modelZoomer.atKey("route");

const routeCurrentItemZoomer:Zoomer<Model, upModel.RouteItem>	=
	routeZoomer.contextualThen((route) =>
		Zoomer.on<Route>().atKey("items").atIndexRo(route.index)
	);

const destinationStateZoomer:Zoomer<Model, DestinationState>	=
	modelZoomer.atKey("destinationState");

const drawerStateZoomer:Zoomer<Model, DrawerState>	=
	modelZoomer.atKey("drawerState");

/**
 * NOTE HERE BE DRAGONS!
 * A CSS transition with transform (in app.less) creates a smooth animation between open and closed drawer.
 * However, this also requires toggling between position: absolute and static with the extra difficulty that the static-Position must be set on transitionEnd.
 *
 * therefore, this emulates onTransitionStart by setting transition when the open state changed.
 * it's reset to false onTransitionEnd below
 */
const drawerModeZoomer:Zoomer<Model, DrawerMode>	=
	drawerStateZoomer.zoom((change) =>
		(current:DrawerState):DrawerState	=> {
			const next	= change(current);
			return {
				open:		next.open,
				content:	next.content,
				transition:	current.open !== next.open,
			};
		}
	);

// -------------------------------------------------------------------------------
// Actions called early on
// -------------------------------------------------------------------------------

export const setScreensize = (size:ScreenSize):void =>
	refresh(modelZoomer.atKey("screenSize").set(size));

export const selectRegionOrTerritory = (item:upModel.Territory|upModel.Region, modalHowtoRegionIds:ReadonlyArray<commonModel.RegionId>):void =>
	refresh((current) => {
		const next:Model	= {
			...current,
			viewRegions:			false,
			modalHowto:				true,
			modalHowtoRegionIds:	modalHowtoRegionIds,
			// TODO up map we do not want a pin here, we only want to pan and zoom!
			setMapViewEvent: setMapViewEvent({
				zoom:		item.zoom,
				center:		item.center,
				pinVisible:	false,
				polygon:	[],
			}),
		};
		return next;
	});

// new destination from map
export const showDestination = async (upData:UpData, languageCode:i18n.LanguageCode, destinationId:commonModel.DestinationId):Promise<void> => {
	tracking.gaSendEvent({
		action:		"planner-show-destination",
		category:	"planner",
		label:		`destination: ${upData.destination.getOrNull(destinationId)?.t ?? "-"}`,
		value:		3,
	});

	try {
		const json:upModel.DesktopDestination	= await Server.getDestination({ lang: languageCode, destinationId: destinationId });

		refresh((current) => {
			// NOTE this is safe because hotelCategory is a FreeSelect so the value is guaranteed to be a Some
			const selectedCat	= Maybe.unsafeGet(PersonalSchema.fields.hotelCategory.extract(current.personalData.hotelCategory));

			const hotel =
				json.hotels.length > 0
				?	selectDefaultHotel(json.hotels, selectedCat)
				:	null;
			const routeItem:upModel.RouteItem =	{
				id:			json.id,
				name:		json.name,
				// TODO night interesting - do we come from here somehow?
				hotel:		hotel,
				nights:		0,
				wish:		"",
				activities:	[],
			};

			const newRoute =
				current.route.items.length === 0	? [ routeItem ]	:
				current.route.added					? Arrays.append(current.route.items)(routeItem)	:
				Arrays.replaceAtIndex(current.route.items)(current.route.items.length - 1)(routeItem);

			const destination	= upData.destination.get(destinationId);
			const region		= upData.region.get(destination.r);

			const next:Model	= {
				...current,

				destination:		json,
				destinationState:	destinationState,

				route:	Fn.tap(updateLocalStorage)({
					items:	newRoute,
					index:	current.route.added || current.route.items.length === 0 ? current.route.items.length : current.route.items.length - 1,
					added:	false,
				}),

				modalHelp:	false,
				modalHowto: false,

				setMapViewEvent:setMapViewEvent({
					zoom:		region.zoom,
					center:		destination.c,
					pinVisible:	true,
					polygon:	destination.p,
				}),
			};

			return drawerModeZoomer.set({ open: true, content: "destination" })(next);
		});
	}
	catch (e) {
		logger.error(`showDestination: Could not load destination with Id ${destinationId}, `, e);
	}
};

// destination from route
export const selectDestination = async (upData:UpData, languageCode:i18n.LanguageCode, destinationId:commonModel.DestinationId, newRouteIndex:number, openDrawer:boolean):Promise<void> => {
	tracking.gaSendEvent({
		action:		"planner-destination-from-route",
		category:	"planner",
		label:		`destination: ${upData.destination.getOrNull(destinationId)?.t ?? "-"}`,
		value:		3,
	});

	try {
		const json:upModel.DesktopDestination	= await Server.getDestination({ lang: languageCode, destinationId: destinationId });

		refresh((current) => {
			const newRouteItem = current.route.items[newRouteIndex];
			// if the current displayed destination is not added,
			// we silently remove it
			const newRoute =
				current.route.added
				?	current.route.items
				:	Arrays.removeAtIndex(current.route.items)(current.route.items.length - 1);

			const destination	= upData.destination.get(destinationId);
			const region		= upData.region.get(destination.r);

			const next:Model	= {
				...current,

				destination:		json,
				destinationState:	{
					destinationGallery:	galleryDefault,
					hotelGallery:		galleryDefault,
					sightsOpen:			false,
					wishOpen:			newRouteItem.wish === "",
					wishTmp:			newRouteItem.wish,
				},

				route:	Fn.tap(updateLocalStorage)({
					items:			newRoute,
					index:			newRouteIndex,
					// if there was an unadded destination, we have removed it so the route is completely added now
					added:			true,
				}),

				modalHelp:	false,
				modalHowto: false,

				setMapViewEvent: setMapViewEvent({
					zoom:		region.zoom,
					center:		destination.c,
					pinVisible:	true,
					polygon:	destination.p,
				}),
			};

			return openDrawer
					?	drawerModeZoomer.set({ open: true, content: "destination" })(next)
					:	next;
		});
	}
	catch (e) {
		logger.error(`selectDestination: Could not load destination with Id ${destinationId}, `, e);
	}
};

export const loadExampleRoute = async (upData:UpData, languageCode:i18n.LanguageCode, exampleRouteId:commonModel.ExampleRouteId|null):Promise<void> => {
	if (exampleRouteId === null) {
		logger.error("Trying to load an example route while not having an exampleroute id.");
		return;
	}

	const route	= upData.route.getOrNull(exampleRouteId);
	if (route === null) {
		logger.error("Trying to load an example route which does not exists in the database any more");
		return;
	}

	await loadRoute(
		upData,
		languageCode,
		exampleRouteId,
		route.destinations[0]
	);
};

// Called from the modal when the user arrived with an exampleroute parameter but has already a route in LocalStorage
export const loadSavedRoute = async (upData:UpData, languageCode:i18n.LanguageCode, destinationId:commonModel.DestinationId):Promise<void> => {
	try {
		const json:upModel.DesktopDestination	= await Server.getDestination({ lang: languageCode, destinationId: destinationId });

		refresh((current) => {
			const newRoute		= current.savedRoute;
			const destination	= upData.destination.get(destinationId);
			const region		= upData.region.get(destination.r);

			const label	= `saved-route: ${newRoute.map(it => it.id).join(",")}`;
			tracking.gaSendEvent({
				action:		"planner-saved-route",
				category:	"planner",
				label:		label,
				value:		3,
			});

			const next:Model	= {
				...current,

				destination:		json,
				destinationState:	{
					destinationGallery:	galleryDefault,
					hotelGallery:		galleryDefault,
					sightsOpen:			false,
					wishOpen:			newRoute[0].wish === "",
					wishTmp:			newRoute[0].wish,
				},

				route:	Fn.tap(updateLocalStorage)({
					items:			newRoute,
					index:			0,
					added:			true,
				}),

				modalHelp:				false,
				modalHowto:				false,
				modalOverwriteRoute:	false,

				setMapViewEvent: setMapViewEvent({
					zoom:		region.zoom,
					center:		destination.c,
					pinVisible:	true,
					polygon:	destination.p,
				}),
			};

			return drawerModeZoomer.set({ open: true, content: "destination" })(next);
		});
	}
	catch (e) {
		logger.error(`loadSavedRoute: Could not load destination with Id ${destinationId}, `, e);
	}
};

// Load example route (when user arrives from a wordpress page linking with parameter exampleRouteId)
export const loadRoute = async (upData:UpData, languageCode:i18n.LanguageCode, exampleRouteId:commonModel.ExampleRouteId, firstDestinationId:commonModel.DestinationId):Promise<void> => {
	tracking.gaSendEvent({
		action:		"planner-example-route",
		category:	"planner",
		label:		`route: ${exampleRouteId}`,
		value:		3,
	});

	// logger.info(exampleRouteId, firstDestinationId);

	try {
		const json:upModel.RouteWithWarnings	= await Server.getRoute({ lang: languageCode, exampleRouteId: exampleRouteId });

		const destination	= upData.destination.get(firstDestinationId);
		const region		= upData.region.get(destination.r);

		refresh((current) => {
			const next:Model	= {
				...current,

				destination:		json.destination,
				destinationState:	destinationState,

				route:	Fn.tap(updateLocalStorage)({
					items:			json.route,
					index:			0,
					added:			true,
				}),

				modalHelp:				false,
				modalHowto:				false,
				modalOverwriteRoute:	false,

				warnings:			json.warnings,

				setMapViewEvent: setMapViewEvent({
					zoom:		region.zoom,
					center:		destination.c,
					pinVisible:	true,
					polygon:	destination.p,
				}),
			};

			return drawerModeZoomer.set({ open: true, content: "destination" })(next);
		});
	}
	catch (e) {
		logger.error(`loadRoute: Could not load route with Id ${exampleRouteId}, `, e);
	}
};

// -------------------------------------------------------------------------------
// Toggle modals and different screen elements
// -------------------------------------------------------------------------------

export const confirmStart = async (upData:UpData, languageCode:i18n.LanguageCode, exampleRouteId:Branded<number, "ExampleRouteId">|null):Promise<void> => {
	toggleStart();
	await loadExampleRoute(upData, languageCode, exampleRouteId);
};

export const toggleStart = ():void =>
	refresh((current) => {
		tracking.gaSendEvent(
			current.modalHowto
			?	{
					action:		"planner-close-howto",
					category:	"planner",
					label:		"close-howto",
					value:		2,
				}
			:	{
					action:		"planner-open-howto",
					category:	"planner",
					label:		"open-howto",
					value:		2,
				}
		);

		return modelZoomer.atKey("modalHowto").mod(Booleans.not)(current);
	});

export const toggleHelp = ():void =>
	refresh((current) => {
		tracking.gaSendEvent(
			current.modalHelp
			?	{
					action:		"planner-close-help",
					category:	"planner",
					label:		"close-help",
					value:		2,
				}
			:	{
					action:		"planner-open-help",
					category:	"planner",
					label:		"open-help",
					value:		2,
				}
			);

		return modelZoomer.atKey("modalHelp").mod(Booleans.not)(current);
	});

export const toggleRouteDelete = ():void =>
	refresh(
		modelZoomer.atKey("modalRouteDelete").mod(Booleans.not),
	);

export const toggleDestinationLightbox = ():void =>
	refresh(
		destinationStateZoomer.atKey("destinationGallery").atKey("open").mod(Booleans.not)
	);

export const toggleHotelLightbox = ():void =>
	refresh(
		destinationStateZoomer.atKey("hotelGallery").atKey("open").mod(Booleans.not)
	);

export const toggleSights = ():void => {
	refresh(
		destinationStateZoomer.atKey("sightsOpen").mod(Booleans.not)
	);
};

export const toggleRouteLandscape = ():void => {
	tracking.gaSendEvent({
		action:		"planner-toggle-route",
		category:	"planner",
		label:		"toggle-route-landscape",
		value:		2,
	});

	toggleViewLandscape("route");
};

export const toggleDestinationLandscape = ():void => {
	tracking.gaSendEvent({
		action:		"planner-toggle-destination",
		category:	"planner",
		label:		"toggle-destination-landscape",
		value:		2,
	});

	toggleViewLandscape("destination");
};

const toggleViewLandscape	= (target:"route"|"destination"):void =>
	refresh(
		drawerModeZoomer.mod(({ open, content }) => ({
			open:		!(open && content === target),
			content:	target,
		}))
	);

export const closePortrait	= ():void	=>
	refresh((current) =>
		(	current.route.items.length !== 0
			?	drawerModeZoomer.set({ open: true, content: "destination" })
			:	drawerModeZoomer.atKey("open").set(false)
		)(current)
	);

export const toggleDrawer = ():void => {
	tracking.gaSendEvent({
		action:		"planner-toggle-drawer",
		category:	"planner",
		label:		"toggle-drawer",
		value:		2,
	});

	refresh(
		drawerModeZoomer.atKey("open").mod(Booleans.not)
	);
};

export const closeDrawer = ():void => {
	tracking.gaSendEvent({
		action:		"planner-close-drawer",
		category:	"planner",
		label:		"close-drawer",
		value:		2,
	});

	refresh(
		drawerModeZoomer.atKey("open").set(false)
	);
};

export const viewDestination = ():void =>
	refresh(
		drawerModeZoomer.set({ open: true, content: "destination" })
	);

export const drawerTransitionEnd = ():void =>
	refresh(
		drawerStateZoomer.atKey("transition").set(false)
	);

// TODO trans would it hurt to use drawerModeZoomer which changes the transition state?
export const toggleDestinationPortrait	= ():void => {
	tracking.gaSendEvent({
		action:		"planner-toggle-route",
		category:	"planner",
		label:		"toggle-route-portrait",
		value:		2,
	});

	refresh(
		drawerStateZoomer.atKey("content").mod((current) =>
			current === "route" ? "destination" : "route"
		)
	);
};

// TODO up does this work with 0 images?
export const galleryStep = (type:"r"|"h", direction:"previous"|"next"):void => {
	if (type === "r") {
		refresh((current) => {
			if (current.destination === null)	return current;
			const destination = current.destination;

			return destinationStateZoomer.atKey("destinationGallery").atKey("index").mod((old) =>
				util.stepOrKeep(old, destination.images.length, direction)
			)(current);
		});
	}
	else if (type === "h") {
		refresh((current) => {
			const currentHotel = routes.currentItem(current.route).hotel;
			if (current.destination === null || current.destination.hotels.length < 1 || currentHotel === null)	return current;

			const hotel	=
				current.destination.hotels.find(it =>
					it.categoryId	=== currentHotel.categoryId &&
					it.siteId		=== currentHotel.siteId
				);
			const hotelImageLength	= hotel !== undefined ? hotel.images.length : 0;

			return destinationStateZoomer.atKey("hotelGallery").atKey("index").mod((old) =>
				util.stepOrKeep(old, hotelImageLength, direction)
			)(current);
		});
	}
	else Fn.impossible(type);
};

// -------------------------------------------------------------------------------
// Route functions
// -------------------------------------------------------------------------------

export const toggleAdded = ():void => {
	refresh((current) => {
		tracking.gaSendEvent(
			current.route.added
			?	{
					action:		"planner-remove-destination",
					category:	"planner",
					label:		"remove-from-destination",
					value:		3,
				}
			:	{
					action:		"planner-add-destination",
					category:	"planner",
					label:		`destination: ${current.destination?.name ?? "-"}`,
					value:		3,
				}
			);
		// TODO route toggle the added one, or the current one?
		const updateNights	=
			routeCurrentItemZoomer
			.atKey("nights")
			.mod(it => current.route.added ? 0 : it);

		const toggleAdded	=
			routeZoomer.atKey("added").mod(Booleans.not);

		return Fn.andThen3(
			updateNights,
			toggleAdded,
			routeZoomer.execute(updateLocalStorage)
		)(current);
	});
};

export const removeFromRoute = (index:number):void => {
	tracking.gaSendEvent({
		action:		"planner-remove-destination",
		category:	"planner",
		label:		"remove-from-route",
		value:		3,
	});

	refresh(
		Fn.andThen(
			routeZoomer.mod((route) => {
				if (index === route.index && !route.added) {
					// This should never be called, instead call removeActiveDestination
					logger.error("active route item can't be removed with this action, call removeActiveDestination instead");
					return route;
				}
				else if (index === route.index && route.added) {
					// if the active routeItem is the last one, it becomes preliminary
					return {
						items:	route.items,
						index:	route.index,
						added:	false,
					};
				}
				else if (index > route.index) {
					// if deleted route item comes AFTER active route item we can simply remove it
					return {
						items:	Arrays.removeAtIndex(route.items)(index),
						index:	route.index,
						added:	route.added,
					};
				}
				else {
					// if deleted route item comes BEFORE active route item, the routeIndex must be shortend too
					return {
						items:	Arrays.removeAtIndex(route.items)(index),
						index:	route.index - 1,
						added:	route.added,
					};
				}
			}),
			routeZoomer.execute(updateLocalStorage)
		)
	);
};

export const removeActiveDestination = async (upData:UpData, languageCode:i18n.LanguageCode, nextId:commonModel.DestinationId):Promise<void> => {
	tracking.gaSendEvent({
		action:		"planner-remove-destination",
		category:	"planner",
		label:		"remove-from-route",
		value:		3,
	});

	try {
		const json:upModel.DesktopDestination	= await Server.getDestination({ lang: languageCode, destinationId: nextId });

		refresh((current) => {
			const nextRouteItem	= current.route.items[current.route.index + 1];
			const newRoute		= Arrays.removeAtIndex(current.route.items)(current.route.index);
			const destination	= upData.destination.get(nextId);
			const region		= upData.region.get(destination.r);

			const next:Model	= {
				...current,

				destination:		json,
				route:	Fn.tap(updateLocalStorage)({
					items:			newRoute,
					index:			current.route.index,
					added:			true,
				}),
				modalHelp:	false,
				modalHowto: false,
				destinationState:	{
					destinationGallery:	galleryDefault,
					hotelGallery:		galleryDefault,
					sightsOpen:			false,
					wishOpen:			nextRouteItem.wish === "",
					wishTmp:			nextRouteItem.wish,
				},
				setMapViewEvent: setMapViewEvent({
					zoom:		region.zoom,
					center:		destination.c,
					pinVisible:	true,
					polygon:	destination.p,
				}),
			};

			return next;
		});
	}
	catch (e) {
		logger.error(`removeActiveDestination: Could not load destination with Id ${nextId}, `, e);
	}
};

export const clearRoute = ():void => {
	tracking.gaSendEvent({
		action:		"planner-clear-route",
		category:	"planner",
		label:		"planner-clear-route",
		value:		3,
	});

	refresh((current) => {
		const next:Model	= {
			...current,

			destination:		null,
			route:				Fn.tap(updateLocalStorage)(routes.empty),
			modalRouteDelete:	false,
			destinationState:	destinationState,
			setMapViewEvent:	null,
		};

		return next;
	});
};

export const moveDestination = (index:number, direction:"up"|"down"):void => {
	tracking.gaSendEvent({
		action:		"planner-move-destination",
		category:	"planner",
		label:		`direction: ${direction}`,
		value:		3,
	});

	refresh(
		Fn.andThen(
			routeZoomer.mod(routes.moveItem(index, direction === "up" ? index -1 : index + 1)),
			routeZoomer.execute(updateLocalStorage)
		)
	);
};

export const setNights = (dir:"up"|"down"):void => {
	refresh((current) => {
		tracking.gaSendEvent({
			action:		"planner-set-nights",
			category:	"planner",
			label:		`direction: ${dir}`,
			value:		3,
		});

		const changeNights	=
			routeCurrentItemZoomer
			.atKey("nights")
			.mod(util.upDown(dir));

		return Fn.andThen(
			changeNights,
			routeZoomer.execute(updateLocalStorage)
		)(current);
	});
};

export const selectHotel = (hotelId:commonModel.HotelId, categoryId:commonModel.HotelCategoryId):void =>
	refresh((current) => {
		// there should not be a button in the GUI to do this in this state
		if (current.route.index < 0 || current.destination === null)	return current;

		tracking.gaSendEvent({
			action:		"planner-select-hotel",
			category:	"planner",
			label:		`hotel: ${hotelId}`,
			value:		3,
		});

		const foundHotel =
			current.destination.hotels.find(it =>
				it.id			=== hotelId &&
				it.categoryId	=== categoryId
			) ?? null;

		const hotel = Nullable.then(foundHotel)(routeHotel);

		const setHotel	=
			routeCurrentItemZoomer
			.atKey("hotel")
			.set(hotel);

		const resetGalleryIndex	=
			destinationStateZoomer.atKey("hotelGallery").atKey("index").set(0);

		return Fn.andThen3(
			setHotel,
			routeZoomer.execute(updateLocalStorage),
			resetGalleryIndex
		)(current);
	});

export const selectActivity = (activityId:upModel.ActivityId):void =>
	refresh((current) => {
		if (current.route.index < 0 || current.destination === null)	return current;

		tracking.gaSendEvent({
			action:		"planner-select-activity",
			category:	"planner",
			label:		`activity: ${activityId}`,
			value:		3,
		});

		const currentActivities	= routes.currentItem(current.route).activities;
		const foundIndex		= currentActivities.findIndex(it => it.id === activityId);
		const newActivity		= current.destination.activities.find(it => it.id === activityId);

		const newActivities:ReadonlyArray<upModel.RouteActivity>	=
			foundIndex >= 0	?
				Arrays.removeAtIndex(currentActivities)(foundIndex)	:
			newActivity !== undefined ?
				Arrays.append<upModel.RouteActivity>(currentActivities)({
					name:		newActivity.name,
					id:			newActivity.id,
					idReduced:	newActivity.idReduced,
				})
			:	currentActivities;

		const setActivities	=
			routeCurrentItemZoomer
			.atKey("activities")
			.set(newActivities);

		const setAdded	=
			routeZoomer.atKey("added").set(true);

		return Fn.andThen3(
			setActivities,
			setAdded,
			routeZoomer.execute(updateLocalStorage)
		)(current);
	});

// -------------------------------------------------------------------------------
// Handle Wish
// -------------------------------------------------------------------------------

export const openWishTextarea = ():void =>
	refresh(destinationStateZoomer.atKey("wishOpen").set(true));

export const setWish = (wish:string):void =>
	refresh(destinationStateZoomer.atKey("wishTmp").set(wish));

// if destination is not added, we add it automatically
export const commitWish = ():void =>
	refresh((current) =>
		changeWish(current.destinationState.wishTmp)(current)
	);

export const removeWish = ():void =>
	refresh(changeWish(""));

const changeWish	= (wish:string):Endo<Model>	=>
	Fn.andThen4(
		destinationStateZoomer.atKey("wishOpen").set(false),
		routeCurrentItemZoomer.atKey("wish").set(wish),
		routeZoomer.atKey("added").set(true),
		routeZoomer.execute(updateLocalStorage),
	);

// -------------------------------------------------------------------------------
// Everything to do with the request form: set fields, toggle modal, send data etc
// -------------------------------------------------------------------------------

export const setFlightEnabled	= (flightEnabled:boolean):void	=>
	refresh(
		Fn.andThen(
			modelZoomer.atKey("flightEnabled").set(flightEnabled),
			flightZoomer.mod(FlightSchema.untouch)
		)
	);

export const modifyExampleRouteForm:Fn<Endo<ExampleRouteModel>, void> =
	Fn.andThen(exampleRouteZoomer.mod, refresh);

export const modifyPersonalForm:Fn<Endo<PersonalModel>, void> =
	Fn.andThen(personalZoomer.mod, refresh);

export const modifyFlightForm:Fn<Endo<FlightModel>, void> =
	Fn.andThen(flightZoomer.mod, refresh);

export const sendRequest = (languageCode: i18n.LanguageCode):void =>
	refresh((model) =>
		Maybe.cata(buildRequestData(languageCode, model))(
			() => {
				// no valid form data available

				window.setTimeout(() => FormDom.exposeFirstError(document), 100);

				return Fn.andThen(
					personalZoomer.mod(PersonalSchema.touch),
					flightZoomer.mod(FlightSchema.touch),
				);
			},
			(requestData) => {
				// form is filled out correctly

				void actuallySendRequest(requestData);

				return Fn.andThen(
					modelZoomer.atKey("requestEnroute").set(true),
					modelZoomer.atKey("modalRequest").set(false),
				);
			}
		)(model)
	);

const buildRequestData	= (languageCode: i18n.LanguageCode, model:Model):Maybe<upModel.RequestData>	=>
	Maybe.map(Model.extractFormData(model))((formData) => {
		const request: upModel.RequestData = {
			lang:		languageCode,
			route:		routes.regularItems(model.route),
			formData: {
				personal:	formData.personal,
				flight:		formData.flight,
			},
			entryPoint:	model.entryPoint,
			warnings:	model.warnings,
		};
		return request;
	});

const actuallySendRequest	= async (requestData:upModel.RequestData) => {
	try {
		const json:upModel.RequestResponse	= await Server.setRequest(requestData);

		tracking.gaSendEvent({
			action:		"planner-submit",
			category:	"planner",
			label:		util.labelEntryPoint(requestData.entryPoint),
			value:		10,
		});

		refresh(Fn.andThen(
			modelZoomer.atKey("requestResponse").set(json),
			modelZoomer.atKey("requestEnroute").set(false)
		));
	}
	catch (e) {
		logger.error("caught", e);
		refresh(Fn.andThen(
			modelZoomer.atKey("requestResponse").set({ return: "error" }),
			modelZoomer.atKey("requestEnroute").set(false)
		));
	}
};

export const toggleRequest = ():void =>
	refresh((current) => {
		const label	= `route: ${current.route.items.map(it => it.id).join(",")}`;
		tracking.gaSendEvent(
			current.modalRequest
			?	{
					action:		"planner-close-request",
					category:	"planner",
					label:		label,
					value:		0,
				}
			:	{
					action:		"planner-open-request",
					category:	"planner",
					label:		label,
					value:		5,
				}
		);

		// TODO switch should only be set when opening the dialog
		// there is more to clean: toggleRequest is mostly used to close the modal without wiping its data
		const updateDuration	=
			PersonalSchema.injectPartial({ duration: calcDuration(current.route.items) });

		return Fn.andThen(
			modelZoomer.atKey("modalRequest").mod(Booleans.not),
			modelZoomer.atKey("personalData").mod(updateDuration),
		)(current);
	});

export const closeRequest = ():void =>
	refresh((current) => {
		const label	= `route: ${current.route.items.map(it => it.id).join(",")}`;
		tracking.gaSendEvent({
			action:		"planner-close-request",
			category:	"planner",
			label:		label,
			value:		0,
		});

		return modelZoomer.atKey("requestResponse").set(null)(current);
	});

// -------------------------------------------------------------------------------
// Local functions
// -------------------------------------------------------------------------------

const selectDefaultHotel = (hotels:ReadonlyArray<upModel.Hotel>, selectedCat:commonModel.HotelCategoryId):upModel.RouteHotel =>
	routeHotel(Maybe.unsafeGet(
		Arrays.minimumWith(hotels)(hotelPreference(selectedCat))
	));

const hotelPreference	= (selectedCat:commonModel.HotelCategoryId):Comparator<upModel.Hotel>	=> {
	// NOTE Booleans.compare sorts false first, we want true (recommended) first
	const preferRecommended:Comparator<upModel.Hotel>	=
		Comparator.contraMap(Booleans.compare)((it:upModel.Hotel) => !it.isRecommendation);

	// NOTE distances grow from small ones (which we want) to large ones (which we do not want)
	const preferSelectedCategory	= (selectedCat:commonModel.HotelCategoryId):Comparator<upModel.Hotel>	=>
		Comparator.contraMap(Numbers.compare)(categoryDistance(selectedCat));

	const categoryDistance	= (selectedCat:number) => (hotel:upModel.Hotel):number	=> {
		// positive for a more expensive hotel as cats are sorted ascending in order of expensiveness
		const diff	= Math.abs(hotel.categoryId - selectedCat);
		// skewed towards more expensive categories by making the distance of cheaper hotels larger
		const dist	=
			hotel.categoryId > selectedCat	?	diff		:
			hotel.categoryId < selectedCat	?	diff * 10	:
			0;
		return dist;
	};

	return Comparator.concat(preferSelectedCategory(selectedCat), preferRecommended);
};

const routeHotel	= (hotel:upModel.Hotel):upModel.RouteHotel	=> ({
	id:				hotel.id,
	categoryId:		hotel.categoryId,
	siteId:			hotel.siteId,
});
